/* eslint-disable jsx-a11y/no-autofocus -- Preserve focus on the sole code-entry control from the legacy OTP component. */
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Auth } from 'aws-amplify'
import { REGEXP_ONLY_DIGITS } from 'input-otp'

import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/InputOTP'
import { useTranslation } from 'react-i18next'

import DrkTrimMainLogo from '../../components/svg/DrkTrimMain'

import { Alert } from '@/actions'

// scss files
import appScss from '../App/App.scss'
import scss from './Login.scss'

export default function Verify(props) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const emailParam = queryParams.get('email')?.trim().toLowerCase() ?? ''
  const codeParam = queryParams.get('code')?.trim() ?? ''

  const [isLoading, setIsLoading] = useState(false)
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [email, setEmail] = useState(emailParam)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordResetNeeded, setPasswordResetNeeded] = useState(false)
  const [confirmationCode, setConfirmationCode] = useState(codeParam)
  const [verifySuccess, setVerifySuccess] = useState(false)

  const dispatch = useDispatch()

  useEffect(() => {
    document.getElementsByTagName('body')[0].className = scss.boxedFormPage
    return () => {
      document.getElementsByTagName('body')[0].className = ''
    }
  }, [])

  const validateCodeForm = () => {
    return email.length > 0 && confirmationCode.length > 5
  }

  const validateResetForm = () => {
    return (
      validateCodeForm() && password.length > 0 && password === confirmPassword
    )
  }

  const handleError = e => {
    setIsLoading(false)

    // console.warn("e is: ",e);
    // console.log("EMAIL",email,"CODE",confirmationCode)

    switch (e.code) {
      case 'UserNotFoundException':
        dispatch(Alert({ error: t('verify.errors.userNotFound') }))
        break
      case 'NotAuthorizedException':
        if (e.message === 'User is disabled.') {
          dispatch(Alert({ error: t('verify.errors.disabled') }))
        } else if (
          e.message ===
            'User cannot be confirmed. Current status is CONFIRMED' ||
          e.message ===
            'User cannot be confirmed. Current status is RESET_REQUIRED'
        ) {
          setPasswordResetNeeded(true)
        } else {
          dispatch(
            Alert({
              error: t('verify.errors.notAuthorized', { message: e.message }),
            })
          )
        }
        break
      case 'ExpiredCodeException':
        dispatch(Alert({ error: t('verify.errors.expiredCode') }))
        break
      default:
        dispatch(
          Alert({
            error: t('verify.errors.confirmationFailed', {
              message: e.message,
            }),
          })
        )
        break
    }
  }

  const handleResendEmail = () => {
    if (email.length <= 0) {
      return dispatch(Alert({ error: t('verify.errors.emailRequired') }))
    }

    const resendCode = passwordResetNeeded
      ? Auth.forgotPassword(email)
      : Auth.resendSignUp(email)

    resendCode
      .then(e => {
        if (e && e.CodeDeliveryDetails) {
          dispatch(Alert({ success: t('verify.success.emailSent') }))
        }
      })
      .catch(e => {
        console.error('ResendEmail', e)
        dispatch(
          Alert({
            error: t('verify.errors.emailVerificationFailed', {
              message: e.message,
            }),
          })
        )
      })
  }

  const handleSignIn = () => {
    setIsSigningIn(true)

    Auth.signIn(email, password)
      .then(loginInfo => {
        setIsLoading(false)
        setIsSigningIn(false)

        const userPayload = loginInfo.signInUserSession.idToken.payload
        const accessToken = loginInfo.signInUserSession.accessToken.jwtToken
        props.handleLoginAttempt(true, userPayload, { accessToken, password })
        navigate('/')
      })
      .catch(() => {
        setIsLoading(false)
        setIsSigningIn(false)
        setVerifySuccess(true)
        dispatch(
          Alert({
            success: t('verify.success.passwordResetLoginAgain'),
          })
        )
      })
  }

  const handleResetPassword = e => {
    e.preventDefault()

    if (password === '') {
      dispatch(Alert({ error: t('verify.errors.passwordRequired') }))
    } else if (password !== confirmPassword) {
      dispatch(Alert({ error: t('verify.errors.passwordMismatch') }))
    } else {
      setIsLoading(true)
      Auth.forgotPasswordSubmit(email, confirmationCode, password)
        .then(() => {
          dispatch(Alert({ success: t('verify.success.loggingIn') }))
          handleSignIn()
        })
        .catch(handleError)
    }
  }

  const handleConfirmationSubmit = e => {
    e.preventDefault()

    setIsLoading(true)

    //Confirm Sign Up
    Auth.confirmSignUp(email, confirmationCode)
      .then(function () {
        setIsLoading(false)
        setVerifySuccess(true)
      })
      .catch(function (err) {
        handleError(err)
      })
  }

  const handleResetPasswordEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleResetPassword(e)
    }
  }

  const handleConfirmationSubmitEnter = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleConfirmationSubmit(e)
    }
  }

  // ==================================================================================== //
  // ======== RENDER THE FIRST SCREEN ALLOWING USERS TO ENTER THEIR EMAIL & CODE ======== //
  // ==================================================================================== //

  const renderVerifyForm = () => {
    const heading = passwordResetNeeded
      ? t('verify.reset.title')
      : t('verify.title')
    const helperText = passwordResetNeeded
      ? t('verify.reset.message')
      : t('verify.confirmationCode.message')

    return (
      <>
        <span
          className={scss['login-heading']}
          style={{ marginBottom: '12px' }}
        >
          {heading}
        </span>
        <span
          className={scss['login-helper-text']}
          style={{ marginBottom: '24px' }}
        >
          {helperText}
        </span>
        <label
          htmlFor='verify-email'
          className={appScss['input-notched']}
          style={{ marginBottom: '24px' }}
        >
          <span>{t('verify.email')}</span>
          <input
            id='verify-email'
            onChange={e => setEmail(e.target.value)}
            onKeyDown={
              passwordResetNeeded
                ? handleResetPasswordEnter
                : handleConfirmationSubmitEnter
            }
            value={email}
            type='email'
            autoComplete='email'
            inputMode='email'
          />
        </label>
        <span
          className={scss['login-helper-text']}
          style={{ marginBottom: '17px' }}
        >
          {t('verify.confirmationCode.resendPrompt')}&nbsp;
          <button
            type='button'
            className={scss['login-link-button-text']}
            onClick={handleResendEmail}
          >
            {t('verify.confirmationCode.resend')}
          </button>
        </span>

        <InputOTP
          autoFocus
          inverse
          maxLength={6}
          pattern={REGEXP_ONLY_DIGITS}
          value={confirmationCode}
          onChange={setConfirmationCode}
          containerClassName='mt-1 w-full'
        >
          <InputOTPGroup className='w-full justify-between'>
            {[0, 1, 2, 3, 4, 5].map(slotIndex => (
              <InputOTPSlot key={slotIndex} index={slotIndex} />
            ))}
          </InputOTPGroup>
        </InputOTP>

        {passwordResetNeeded && (
          <>
            <label
              htmlFor='verify-password'
              className={appScss['input-notched']}
              style={{ marginTop: '32px' }}
            >
              <span>{t('verify.password')}</span>
              <input
                id='verify-password'
                onChange={e => setPassword(e.target.value)}
                onKeyDown={
                  passwordResetNeeded
                    ? handleResetPasswordEnter
                    : handleConfirmationSubmitEnter
                }
                value={password}
                type='password'
              />
            </label>
            <label
              htmlFor='verify-password-confirm'
              className={appScss['input-notched']}
              style={{ marginTop: '32px' }}
            >
              <span>{t('verify.confirmPassword')}</span>
              <input
                id='verify-password-confirm'
                onChange={e => setConfirmPassword(e.target.value)}
                onKeyDown={
                  passwordResetNeeded
                    ? handleResetPasswordEnter
                    : handleConfirmationSubmitEnter
                }
                value={confirmPassword}
                type='password'
              />
            </label>
          </>
        )}
        <button
          type='button'
          onClick={
            passwordResetNeeded ? handleResetPassword : handleConfirmationSubmit
          }
          disabled={
            passwordResetNeeded ? !validateResetForm() : !validateCodeForm()
          }
          className={[
            scss['login-submit-button'],
            scss[isLoading ? 'login-submit-button-loading' : ''],
          ].join(' ')}
          style={{ marginTop: '24px' }}
        >
          {isLoading
            ? isSigningIn
              ? t('verify.reset.signingIn')
              : t('verify.confirmationCode.verifying')
            : t('verify.confirmationCode.verify')}
        </button>
        <Link
          className={scss['login-link']}
          style={{ marginTop: '12px' }}
          to='/login'
        >
          {t('verify.backToLogin')}
        </Link>
      </>
    )
  }

  const renderSuccessMessage = () => {
    return (
      <>
        <span
          className={scss['login-heading']}
          style={{ marginBottom: '12px' }}
        >
          {passwordResetNeeded
            ? t('verify.reset.successTitle')
            : t('verify.success.title')}
        </span>
        <span
          className={[
            scss['login-helper-text'],
            appScss['text-align-center'],
          ].join(' ')}
          style={{ marginBottom: '27px' }}
        >
          {passwordResetNeeded
            ? t('verify.success.passwordResetLoginAgain')
            : t('verify.success.emailVerified')}
        </span>
        <Link
          className={scss['login-link-button']}
          to={
            email
              ? `/login?email=${encodeURIComponent(email.trim().toLowerCase())}`
              : '/login'
          }
        >
          {t('verify.success.login')}
        </Link>
      </>
    )
  }

  return (
    <div className={scss['login-container']}>
      <DrkTrimMainLogo
        className={scss['login-logo']}
        style={{ marginBottom: '54px' }}
        role='img'
        aria-label='MyAssetMap Logo'
      />
      {!verifySuccess ? renderVerifyForm() : renderSuccessMessage()}
    </div>
  )
}
